import { useState } from 'react';
import { Star } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { SectionCard } from '@/components/common/SectionCard';
import { RenewalOfferPanel } from '@/components/common/RenewalOfferPanel';
import { BillingPageLayout } from './components/BillingPageLayout';
import { useEntity } from '@/hooks/useEntity';
import { useDataRoomAccess } from '@/hooks/useDataRoomAccess';
import { useRenewalOffer } from '@/hooks/useRenewalOffer';
import { useStartRenewal } from '@/hooks/useStartRenewal';
import type { DataRoomService } from '@/lib/types/data-room';
import { getApiErrorCode } from '@/lib/utils/error.utils';
import { AutoRenewDialog } from './components/AutoRenewDialog';
import { CurrentPlanCard } from './components/CurrentPlanCard';
import { BillingHistoryTable } from './components/BillingHistoryTable';
import { CancelSubscriptionDialog } from './components/CancelSubscriptionDialog';
import { PremiumPackageCard } from './components/PremiumPackageCard';
import { RenewalScheduledCard } from './components/RenewalScheduledCard';
import { BILLING_STRINGS } from './constants/billing-strings';
import { useBillingActions } from './hooks/useBillingActions';
import { useInvoices } from './hooks/useInvoices';
import { useSubscription } from './hooks/useSubscription';

const COPY = BILLING_STRINGS.OVERVIEW;

/** The backend's answer when this startup has never purchased. */
const NO_SUBSCRIPTION = 'SUBSCRIPTION_NOT_FOUND';
/** Premium is a grant, shown as a package rather than a billed plan (FR-136). */
const PREMIUM: DataRoomService = 'premium';

/**
 * Billing & Info (FR-41, MYN-113; v1.2 FR-131, FR-136).
 *
 * ## What the page shows, decided once
 *
 * - SR NEXT Premium is current: the package card, with no payment features,
 *   and any earlier Issue Report receipts (FR-136).
 * - A purchase exists (an Issue Report, or a recurring plan): the plan card
 *   and the billing history.
 * - Nothing was ever purchased: an empty purchase history, preceded by a
 *   "Premium has ended" card when that is why access stopped (MYN-284).
 *
 * "Never purchased" is the overview's `SUBSCRIPTION_NOT_FOUND`, read by error
 * CODE rather than status, because a 404 from a broken route must not be shown
 * to a paying customer as "you have no access".
 *
 * ## Nothing here is derived
 *
 * Whether cancellation and auto-renewal are offered comes from `canCancel` /
 * `canManageAutoRenew`; seats and end dates come from the access view. A
 * one-time plan has nothing to renew (A-78), and if this page worked that out
 * for itself the rule would live in two repos.
 */
export function BillingPage() {
  const entity = useEntity();
  const subscription = useSubscription();
  const invoices = useInvoices();
  const access = useDataRoomAccess(entity?.entityId ?? null);
  const actions = useBillingActions(entity?.entityId);
  // The renewal after the SR NEXT package (MYN-247, FR-295); this page is Startup Admins only.
  const renewalOffer = useRenewalOffer(entity?.entityId ?? null);
  const subscribe = useStartRenewal(entity?.entityId ?? null);

  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [autoRenewTarget, setAutoRenewTarget] = useState<boolean | null>(null);

  // Inside the page layout, so the spinner and the error sit below the header
  // with the same padding as the content, not jammed against the top bar.
  if (subscription.isLoading || access.isLoading) {
    return (
      <BillingPageLayout title={COPY.TITLE}>
        <LoadingSpinner />
      </BillingPageLayout>
    );
  }

  const current = access.data?.current ?? null;
  // Shown whatever else is on record: an earlier Issue Report purchase leaves a
  // subscription row behind, and must not hide that Premium has ended.
  const lastEnded = access.data?.lastEnded;
  const premiumEndedAt = !current && lastEnded?.service === PREMIUM ? lastEnded.endedAt : null;
  const neverPurchased =
    subscription.isError && getApiErrorCode(subscription.error) === NO_SUBSCRIPTION;
  if (access.isError || (subscription.isError && !neverPurchased)) {
    return (
      <BillingPageLayout title={COPY.TITLE}>
        <ErrorState title={COPY.TITLE} />
      </BillingPageLayout>
    );
  }

  const header = current ? (
    <Badge variant="green-solid" icon={<Star aria-hidden="true" className="h-3.5 w-3.5" />}>
      {COPY.SERVICE_LABELS[current.service]}
    </Badge>
  ) : undefined;
  const history = (
    <SectionCard title={COPY.HISTORY_TITLE}>
      {invoices.isLoading ? <LoadingSpinner size="sm" /> : <BillingHistoryTable invoices={invoices.data ?? []} />}
    </SectionCard>
  );

  const renewal = (
    <RenewalOfferPanel
      offer={renewalOffer.data}
      onSubscribe={(planId) => subscribe.mutate(planId)}
      pendingPlanId={subscribe.isPending ? (subscribe.variables ?? null) : null}
    />
  );

  if (current?.service === PREMIUM) {
    // A renewal bought during the package waits for its end (FR-297): a live
    // recurring subscription while Premium is current can only be that.
    const live = subscription.data;
    const scheduled =
      live && live.status === 'active' && live.billingInterval !== 'one_time' ? live : null;
    return (
      <BillingPageLayout title={COPY.TITLE} headerRight={header}>
        <PremiumPackageCard access={current} />
        {scheduled ? (
          <RenewalScheduledCard planName={scheduled.planName} startsAt={current.endsAt} />
        ) : (
          renewal
        )}
        {(invoices.data?.length ?? 0) > 0 && history}
      </BillingPageLayout>
    );
  }

  const overview = subscription.data;
  if (neverPurchased || !overview) {
    return (
      <BillingPageLayout title={COPY.TITLE}>
        {premiumEndedAt && <PremiumPackageCard endedAt={premiumEndedAt} />}
        {renewal}
        {/* A plain empty history, not an access warning or a sales pitch: the
            client found "no data room access" plus the Issue Report explainer
            confusing on a billing page (UX round 2, MYN-284). Buying stays on
            the navbar CTA and the dashboard card. */}
        <SectionCard title={COPY.HISTORY_TITLE}>
          <EmptyState title={COPY.NO_PURCHASES} />
        </SectionCard>
      </BillingPageLayout>
    );
  }

  return (
    <BillingPageLayout title={COPY.TITLE} headerRight={header}>
      <p className="text-text-secondary -mt-2 text-sm">{COPY.SUBTITLE}</p>

      {premiumEndedAt && <PremiumPackageCard endedAt={premiumEndedAt} />}
      {renewal}

      <CurrentPlanCard
        overview={overview}
        includedSeats={current?.seatLimit ?? 1}
        onAutoRenewChange={(next) => setAutoRenewTarget(next)}
        onCancel={() => setCancelDialogOpen(true)}
        onManagePayment={() => actions.portal.mutate()}
        isOpeningPortal={actions.portal.isPending}
      />

      {history}

      <CancelSubscriptionDialog
        open={cancelDialogOpen}
        onOpenChange={setCancelDialogOpen}
        accessEndsAt={overview.currentPeriodEnd}
        mutation={actions.cancel}
      />

      <AutoRenewDialog
        open={autoRenewTarget !== null}
        onOpenChange={(next) => !next && setAutoRenewTarget(null)}
        turningOn={autoRenewTarget ?? false}
        periodEndsAt={overview.currentPeriodEnd}
        mutation={actions.autoRenew}
      />
    </BillingPageLayout>
  );
}
